import type {
  CrawlJobStatusDTO,
  CrawlStatus,
  DocumentStatus,
} from '../../types/knowledge';

/**
 * Polling intervals for knowledge detail tabs.
 * Change these values to tune refresh frequency.
 */
export const KNOWLEDGE_DOCUMENT_POLL_INTERVAL_MS = 15_000;

const TERMINAL_DOCUMENT_STATUSES = new Set<DocumentStatus>(['READY', 'FAILED']);

/** Crawl statuses that still need status updates while the crawls tab is open. */
export const NEEDS_ATTENTION_CRAWL_STATUSES = new Set<CrawlStatus>([
  'AWAITING_SELECTION',
]);

export const CRAWLS_MAPPING_GROUP_STATUSES = new Set<CrawlStatus>(['MAPPING']);

export const CRAWLS_IN_PROGRESS_GROUP_STATUSES = new Set<CrawlStatus>([
  'CRAWLING',
  'PROCESSING',
]);

const TERMINAL_CRAWL_STATUSES = new Set<CrawlStatus>(['COMPLETED', 'FAILED']);

export function isTerminalCrawlStatus(
  status: CrawlStatus | null | undefined,
): boolean {
  return status != null && TERMINAL_CRAWL_STATUSES.has(status);
}

/** Prefer live SSE status over the crawls list snapshot for UI grouping. */
export function getEffectiveCrawlStatus(
  crawl: Readonly<{ status?: CrawlStatus | null }>,
  liveStatus?: CrawlJobStatusDTO | null,
): CrawlStatus | null | undefined {
  return liveStatus?.status ?? crawl.status;
}

/** Abandoned worker work that is eligible for resume via the resume-abandoned API. */
export function isAbandonedCrawl(
  crawl: Readonly<{ abandoned?: boolean | null }>,
  liveStatus?: Readonly<{ abandoned?: boolean | null }> | null,
): boolean {
  // REST list/detail data wins over stale SSE snapshots (e.g. after resume-abandoned).
  if (crawl.abandoned === false) {
    return false;
  }

  return liveStatus?.abandoned === true || crawl.abandoned === true;
}

/** Documents that still need list polling (excludes EMBEDDING, which uses SSE). */
export function hasDocumentsNeedingPoll(
  documents: ReadonlyArray<{ status?: DocumentStatus | null }>,
): boolean {
  return documents.some((document) => {
    const status = document.status;
    // Missing status is unknown — do not poll forever on incomplete rows.
    if (status == null) {
      return false;
    }
    if (TERMINAL_DOCUMENT_STATUSES.has(status)) {
      return false;
    }
    return status !== 'EMBEDDING';
  });
}

export function isAbandonedDocument(
  document: Readonly<{ abandoned?: boolean | null }>,
): boolean {
  return document.abandoned === true;
}
